import { Component, OnInit, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { AuthQuery } from '@app/auth/auth.query';
import { StandortService, Standort } from '../../../services/standort.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../@shared/dialog/confirm-dialog/confirm-dialog.component';

export interface EditGebaeudeData {
  id: string;
  name: string;
  standortId: string;
  status?: number;
}

@Component({
  selector: 'app-edit-gebaeude-dialog',
  templateUrl: './edit-gebaeude-dialog.component.html',
  styleUrls: ['./edit-gebaeude-dialog.component.css'],
})
export class EditGebaeudeDialogComponent implements OnInit {
  frmGebaeude: UntypedFormGroup;
  gebaeudeId: string;
  standortId: string;
  status: number = 1; // Default to active
  standorte: Standort[] = [];
  isLoadingStandorte = false;
  currentStandortName: string = '';

  constructor(
    public dialogRef: MatDialogRef<EditGebaeudeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditGebaeudeData,
    private _fb: UntypedFormBuilder,
    private authQuery: AuthQuery,
    private standortService: StandortService,
    private dialog: MatDialog
  ) {
    this.gebaeudeId = data.id;
    this.standortId = data.standortId;
    this.status = data.status ?? 1; // Default to active if not provided

    // Initialize form - standort_id validators will be set in ngOnInit if user has permission
    this.frmGebaeude = this._fb.group({
      name: [data.name, [Validators.required, Validators.minLength(2)]],
      standort_id: [this.standortId]
    });
  }

  ngOnInit() {
    // Load standorte if user has permission and update validators
    if (this.canMoveGebaeude()) {
      // Add required validator for standort_id if user can move gebaeude
      this.frmGebaeude.get('standort_id')?.setValidators([Validators.required]);
      this.frmGebaeude.get('standort_id')?.updateValueAndValidity();
      this.loadStandorte();
    }
  }

  /**
   * Check if current user is superuser (role 1) or companyadmin (role 2)
   */
  canMoveGebaeude(): boolean {
    const role = this.authQuery.role;
    return role === 1 || role === 2;
  }

  /**
   * Load all standorte sorted alphabetically
   */
  loadStandorte(): void {
    this.isLoadingStandorte = true;
    this.standortService.getStandorte(1, 10000, '', 'name', 'asc', undefined, 1).subscribe({
      next: (response) => {
        this.standorte = (response.data || []).sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        // Find current standort name
        const currentStandort = this.standorte.find(s => s.id === this.standortId);
        this.currentStandortName = currentStandort?.name || '';
        
        this.isLoadingStandorte = false;
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
        this.isLoadingStandorte = false;
      }
    });
  }

  onConfirm(): void {
    if (this.frmGebaeude.valid) {
      this.dialogRef.close({ 
        event: 'update', 
        data: {
          id: this.gebaeudeId,
          ...this.frmGebaeude.value
        }
      });
    }
  }

  onDelete(): void {
    const confirmed = confirm('Sind Sie sicher, dass Sie dieses Gebäude deaktivieren möchten? Es wird als inaktiv markiert und in der Historie angezeigt.');
    if (confirmed) {
      this.dialogRef.close({ 
        event: 'delete', 
        data: { 
          id: this.gebaeudeId,
          name: this.frmGebaeude.get('name')?.value || this.data.name
        }
      });
    }
  }

  onDismiss(): void {
    this.dialogRef.close({ event: 'cancel' });
  }

  isInactive(): boolean {
    return this.status === 0;
  }

  onRestore(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: new ConfirmDialogModel(
        'Gebäude wiederherstellen',
        'Soll das Gebäude wiederhergestellt werden?',
        'Abbrechen',
        'Wiederherstellen'
      )
    });

    confirmDialog.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dialogRef.close({
          event: 'restore',
          data: {
            id: this.gebaeudeId,
            status: 1
          }
        });
      }
    });
  }
}

